import {
  INDICATOR_DOT_BASE_PX,
  INDICATOR_FILL_OPACITY,
  INDICATOR_MARKER_BASE_PX,
  INDICATOR_MARKER_GAP_PX,
  INDICATOR_SHAPE_PX_PER_WIDTH,
} from "../constants";
import { toThemeColor } from "../theme";
import { MARKER_SHAPE } from "../gl/markerRenderer";
import { FILL_BETWEEN_LEVELS, PLOT_TYPE } from "./indicatorTypes";
import { plotShift } from "./indicatorSettings";

const rgbaOf = (cssColor) => toThemeColor(cssColor).rgba;

const withFillOpacity = (cssColor) => {
  const [red, green, blue, alpha] = rgbaOf(cssColor);

  return [red, green, blue, alpha * INDICATOR_FILL_OPACITY];
};

const lineOrColumns = (plot, style, inputs) => ({
  key: plot.key,
  shift: plotShift(plot, inputs),
  colorRule: plot.colorRule,
  color: rgbaOf(style.color),
  downColor: style.downColor ? rgbaOf(style.downColor) : null,
  width: style.width,
});

// The background takes the shift of its lines (they share it)
const fill = (plot, style, definition, inputs) => {
  const firstLine = definition.plots.find((item) => item.key === plot.between[0]);

  return {
    key: plot.key,
    between: plot.between,
    shift: plotShift(firstLine, inputs),
    color: withFillOpacity(style.color),
  };
};

// Visible levels of an oscillator and the background between its first and
// last level (CCI), with the range they cover for the autoscale of the pane
const shownLevels = (definition, styles) => {
  const lines = styles.levels
    .filter((item) => item.isVisible)
    .map(({ value, color, width }) => ({ value, color: rgbaOf(color), width }));
  const levelFill = definition.plots.find(
    (plot) => plot.type === PLOT_TYPE.FILL && plot.between === FILL_BETWEEN_LEVELS,
  );
  const edges = [styles.levels[0], styles.levels[styles.levels.length - 1]].map((item) => item?.value);
  const fillStyle = levelFill ? styles.plots[levelFill.key] : null;
  const hasFill = Boolean(fillStyle?.isVisible) && styles.levels.length > 1;
  const fillBand = hasFill
    ? { top: Math.max(...edges), bottom: Math.min(...edges), color: withFillOpacity(fillStyle.color) }
    : null;
  const values = lines.map((item) => item.value).concat(hasFill ? edges : []);
  const range = values.length ? { min: Math.min(...values), max: Math.max(...values) } : null;

  return { lines, fill: fillBand, range };
};

const marker = (plot, style, inputs) => {
  const isDot = plot.type === PLOT_TYPE.DOTS;
  const size = (isDot ? INDICATOR_DOT_BASE_PX : INDICATOR_MARKER_BASE_PX) + style.width * INDICATOR_SHAPE_PX_PER_WIDTH;
  const offset = isDot ? 0 : INDICATOR_MARKER_GAP_PX + size / 2;
  let shape = MARKER_SHAPE.CIRCLE;

  if (!isDot) shape = plot.isAbove ? MARKER_SHAPE.ARROW_UP : MARKER_SHAPE.ARROW_DOWN;

  return {
    key: plot.key,
    shift: plotShift(plot, inputs),
    shape,
    size,
    // Above the high goes up (negative y), under the low down
    offset: plot.isAbove ? -offset : offset,
    color: rgbaOf(style.color),
  };
};

/**
 * Visible plots of an indicator, grouped by how they are drawn, with
 * parsed colours, shifts and shape sizes (indicatorRenderer).
 *
 * @param {object} definition
 * @param {{ plots: object }} styles - mergeStyles
 * @param {object} inputs
 * @returns {{ lines: object[], histograms: object[], fills: object[], markers: object[], valuePlots: object[],
 *   levels: { lines: object[], fill: object|null, range: object|null } }}
 *   valuePlots: { key, shift, type } of every drawn value, for the autoscale and the price base;
 *   levels: lines { value, color } and the background { top, bottom, color } between the levels
 *   (levelRenderer), range { min, max } of both for the autoscale
 */
export const buildShownPlots = (definition, styles, inputs) => {
  const visible = definition.plots.filter((plot) => styles.plots[plot.key].isVisible);
  const ofTypes = (...types) => visible.filter((plot) => types.includes(plot.type));
  const lines = ofTypes(PLOT_TYPE.LINE).map((plot) => lineOrColumns(plot, styles.plots[plot.key], inputs));
  const histograms = ofTypes(PLOT_TYPE.HISTOGRAM).map((plot) => ({
    ...lineOrColumns(plot, styles.plots[plot.key], inputs),
    type: PLOT_TYPE.HISTOGRAM,
  }));
  const markers = ofTypes(PLOT_TYPE.DOTS, PLOT_TYPE.MARKERS).map((plot) =>
    marker(plot, styles.plots[plot.key], inputs),
  );
  const fills = ofTypes(PLOT_TYPE.FILL)
    .filter((plot) => Array.isArray(plot.between))
    .map((plot) => fill(plot, styles.plots[plot.key], definition, inputs));
  const fillEdges = fills.flatMap(({ between, shift }) => between.map((key) => ({ key, shift })));

  return {
    lines,
    histograms,
    fills,
    markers,
    valuePlots: [...lines, ...histograms, ...markers, ...fillEdges],
    levels: shownLevels(definition, styles),
  };
};
